import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { ICreateSessionRequestPayload } from "src/Types/module/b2bSession/b2bSessionRequest.types";
import B2BSearchSelect from "./B2BSearchSelect";
import SessionSelectField from "./SessionSelectField";

interface Props {
	open: boolean;
	handleClose: () => void;
}

const CreateSessionRequestModal: React.FC<Props> = ({ open, handleClose }) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const [fromB2bId, setFromB2bId] = useState<number | null>(null);
	const [toB2bId, setToB2bId] = useState<number | null>(null);
	const [sessionId, setSessionId] = useState<number | "">("");
	const [status, setStatus] = useState<string>("pending");
	const [note, setNote] = useState<string>("");
	const [formError, setFormError] = useState<string | null>(null);

	const { mutate: createRequest, isPending } = useApiMutation<
		ICreateSessionRequestPayload,
		any
	>({
		method: "POST",
		endPoint: "/api/fair/admin/sessions/requests",
		invalidateQueries: ["SessionRequests", "SessionRequest"],
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);

		if (!fromB2bId) {
			setFormError("Please select 'From B2B' member.");
			return;
		}

		if (!toB2bId) {
			setFormError("Please select 'To B2B' member.");
			return;
		}

		if (fromB2bId === toB2bId) {
			setFormError("'From B2B' and 'To B2B' cannot be the same member.");
			return;
		}

		if (!sessionId) {
			setFormError("Please select a session slot.");
			return;
		}

		const payload: ICreateSessionRequestPayload = {
			from_b2b_id: fromB2bId,
			to_b2b_id: toB2bId,
			session_id: Number(sessionId),
			status: status || "pending",
			note: note.trim() || undefined,
		};

		createRequest(payload, {
			onSuccess: () => {
				handleModalClose();
			},
			onError: (err: any) => {
				console.error(err);
			},
		});
	};

	const handleModalClose = () => {
		setFromB2bId(null);
		setToB2bId(null);
		setSessionId("");
		setStatus("pending");
		setNote("");
		setFormError(null);
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleModalClose}
			fullScreen={fullScreen}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 3,
					py: 2,
				}}
			>
				<Typography variant="h5" fontWeight={700}>
					Create Session Request
				</Typography>
				<IconButton onClick={handleModalClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<Box component="form" onSubmit={handleSubmit}>
				<DialogContent sx={{ p: 3 }}>
					{formError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formError}
						</Alert>
					)}

					<Grid container spacing={2.5}>
						{/* From B2B */}
						<Grid item xs={12}>
							<B2BSearchSelect
								label="From B2B (Requester) *"
								placeholder="Search requester B2B..."
								value={fromB2bId}
								onChange={(id) => setFromB2bId(id)}
								disabledId={toB2bId}
								required
							/>
						</Grid>

						{/* To B2B */}
						<Grid item xs={12}>
							<B2BSearchSelect
								label="To B2B (Invited Member) *"
								placeholder="Search invited B2B..."
								value={toB2bId}
								onChange={(id) => setToB2bId(id)}
								disabledId={fromB2bId}
								required
							/>
						</Grid>

						{/* Session Selection */}
						<Grid item xs={12}>
							<SessionSelectField
								label="Select Session *"
								value={sessionId}
								onChange={(id) => setSessionId(id)}
								required
							/>
						</Grid>

						{/* Status */}
						<Grid item xs={12}>
							<FormControl fullWidth size="small">
								<InputLabel id="status-label" shrink>
									Status
								</InputLabel>
								<Select
									labelId="status-label"
									value={status}
									label="Status"
									notched
									onChange={(e) => setStatus(e.target.value)}
								>
									<MenuItem value="pending">Pending</MenuItem>
									<MenuItem value="approved">Approved</MenuItem>
									<MenuItem value="rejected">Rejected</MenuItem>
									<MenuItem value="completed">Completed</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{/* Note */}
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Meeting Note / Purpose"
								placeholder="e.g. Meeting regarding business expansion"
								multiline
								rows={3}
								size="small"
								InputLabelProps={{ shrink: true }}
								value={note}
								onChange={(e) => setNote(e.target.value)}
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<Divider />

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button variant="outlined" onClick={handleModalClose} disabled={isPending}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={isPending}>
						{isPending ? "Creating..." : "Create Request"}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default CreateSessionRequestModal;
